import { Tooltip, makeStyles, Icon } from '@material-ui/core';
import React from 'react';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

export interface CustomTooltipProps {
  text: string;
  driverName: string;
  meddiccElements?: any[];
  isPoorRep?: boolean;
}

// Fallback MEDDPICC driver definitions for short codes from FastAPI
const MEDDPICC_DEFINITIONS: Record<string, string> = {
  m: 'The quantifiable business outcomes your solution can influence.',
  e: 'The person with budget authority who can sign off on the purchase decision.',
  d: 'The formal and informal criteria the prospect uses to evaluate and select a solution.',
  d_proc: 'The sequence of steps, approvals, and stakeholders involved before a deal is closed.',
  i: "The specific business problem or challenge driving the prospect's urgency to act.",
  c: 'An internal advocate who believes in your solution and actively influences the buying process on your behalf.',
  comp: 'The alternatives the prospect is considering, including competitors, incumbents and the "do nothing" option.',
};

// Full driver name → definition mapping
const DRIVER_DEFINITIONS: Record<string, string> = {
  'MEDDICC Score (Avg)': 'Average score indicating opportunity qualification quality based on MEDDICC criteria.',
  'MEDDICC Score': 'Average score indicating opportunity qualification quality based on MEDDICC criteria.',
  'MEDDICC Adoption Rate': 'Percentage of closed won deals where MEDDICC was actively adopted.',
  'MEDDICC Qualification Strength': 'Weighted average of deal qualification strength.',
  'Scoring Performance (Avg)': 'Average MindTickle module scoring performance.',
  'Scoring Performance': 'Average MindTickle module scoring performance.',
  'Module Status Score': 'Average learner module status score across assigned modules.',
  'Module Status Score (IN)': 'Average module completion progress score for the current quarter.',
  'Module Status Score (C)': 'Average module completion progress score through the current quarter.',
  'Competency Depth Score': 'Weighted score of completed modules, factoring in module type.',
  'Competency Depth Score (IN)': 'Weighted score of completed modules during the current quarter, factoring in module type.',
  'Competency Depth Score (C)': 'Weighted score of completed modules through the current quarter, factoring in module type.',
  'Scoring Performance (Avg) (IN)': 'Average assessment/quiz score across all assigned modules regardless of completion status for the current quarter.',
  'Scoring Performance (Avg) (C)': 'Average assessment/quiz score across all assigned modules regardless of completion status through the current quarter.',
  'Modules Completed (IN)': 'Total number of modules completed by the rep during the current quarter.',
  'Modules Completed (C)': 'Total number of modules completed by the rep through the current quarter.',
  'Tenure (Months)': 'Employee tenure at NetApp in months.',
  'Tenure': 'Employee tenure at NetApp in months.',
  'Reattempt Count (Avg)': 'Average number of attempts per module.',
  'Reattempt Count': 'Average number of attempts per module.',
  'Training Completion Rate': 'Percentage of assigned modules that have been fully completed.',
  'Training Completion Rate (C)': 'Percentage of assigned modules that have been fully completed through the current quarter.',
  'Training Completion Rate (IN)': 'Percentage of assigned modules that have been fully completed in the current quarter.',
  'Metrics Scoring (Avg)': 'Average score for the Metrics (M) element of MEDDICC across CW deals.',
  'Economic Buyer Scoring (Avg)': 'Average score for the Economic Buyer (E) element of MEDDICC across CW deals.',
  'Decision Criteria Scoring (Avg)': 'Average score for the Decision Criteria (D) element of MEDDICC across CW deals.',
  'Decision Process Scoring (Avg)': 'Average score for the Decision Process (D) element of MEDDICC across CW deals.',
  'Identify Pain Scoring (Avg)': 'Average score for the Identify Pain (I) element of MEDDICC across CW deals.',
  'Champion Scoring (Avg)': 'Average score for the Champion (C) element of MEDDICC across CW deals.',
  'Competition Scoring (Avg)': 'Average score for the Competition (C) element of MEDDICC across CW deals.',
};

const MeddiccTable: React.FC<{ elements: any[]; isPoorRep: boolean; definition?: string }> = ({ elements, isPoorRep, definition }) => {
  return (
    <div style={{ fontFamily: 'Rubik', minWidth: 320, padding: '2px 0' }}>
      {definition && (
        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 14, lineHeight: 1.5 }}>
          {definition}
        </div>
      )}
      {/* Table wrapped in a rounded border card */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f3f4f6' }}>
              <th style={{
                textAlign: 'left',
                fontSize: 13,
                fontWeight: 600,
                color: '#111827',
                padding: '10px 14px',
              }}>
                MEDDICC Element
              </th>
              <th style={{
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 600,
                color: '#111827',
                padding: '10px 14px',
                width: 64,
              }}>
                Score
              </th>
              <th style={{
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 600,
                color: '#111827',
                padding: '10px 14px',
                width: 52,
              }}>
                Avg
              </th>
            </tr>
          </thead>
          <tbody>
            {elements.map((el: any, idx: number) => {
              const score = isPoorRep ? el.driver_value_poor : el.driver_value_good;
              const avg = el.avg;
              const scoreColor = score > avg ? '#10b981' : score < avg ? '#ef4444' : '#6b7280';
              return (
                <tr key={idx} style={{ borderTop: '1px solid #f3f4f6', backgroundColor: 'white' }}>
                  <td style={{ fontSize: 13, color: '#111827', padding: '10px 14px' }}>
                    {el.element_name}
                  </td>
                  <td style={{ fontSize: 13, fontWeight: 600, color: scoreColor, textAlign: 'center', padding: '10px 14px' }}>
                    {typeof score === 'number' ? score.toFixed(1) : score}
                  </td>
                  <td style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', padding: '10px 14px' }}>
                    {typeof avg === 'number' ? avg.toFixed(1) : avg}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  text,
  driverName,
  meddiccElements,
  isPoorRep,
}: CustomTooltipProps) => {
  const useStyles = makeStyles({
    active: {
      color: '#0B69FF',
      margin: '0 0 2px 3px',
      fontSize: 17,
      fontFamily: 'Rubik',
      fontWeight: 500,
      textTransform: 'none',
    },
    customTooltip: {
      '& .MuiTooltip-arrow': {
        color: '#0B69FF',
      },
      backgroundColor: 'white',
      fontFamily: 'Rubik',
      border: '1px solid #0B69FF',
      color: '#171F46',
      fontWeight: 300,
      fontSize: '13px',
      maxWidth: 400,
    },
    meddiccTooltip: {
      '& .MuiTooltip-arrow': {
        color: '#e5e7eb',
      },
      backgroundColor: 'white',
      fontFamily: 'Rubik',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      color: '#111827',
      fontWeight: 400,
      fontSize: '13px',
      maxWidth: 420,
      padding: '14px 16px',
    },
  });

  const classes = useStyles();

  // Show MEDDICC breakdown table when elements are provided
  if (meddiccElements && meddiccElements.length > 0) {
    const driver = driverName?.split('(')[0].trim();
    const definition = DRIVER_DEFINITIONS[driverName] || DRIVER_DEFINITIONS[driver];
    return (
      <Tooltip
        placement="top"
        arrow
        classes={{ tooltip: classes.meddiccTooltip }}
        title={<MeddiccTable elements={meddiccElements} isPoorRep={isPoorRep ?? true} definition={definition} />}
        interactive
      >
        <Icon component={InfoOutlinedIcon} className={classes.active} />
      </Tooltip>
    );
  }

  const config = JSON.parse(localStorage.getItem("mappingConfig")||"{}")
  const driverMapping = config.driverMapping
  const driver = driverName?.split('(')[0].trim()
  const driverLower = driver?.toLowerCase()
  // Extract short code before colon, e.g. "I:Identify Pain" -> "i"
  const shortCode = driverLower?.split(':')[0].trim()
  const title = text
    || driverMapping?.find((d:any)=>d?.label == driver || d?.label == driverName || d?.value?.toLowerCase() === shortCode)?.definition
    || DRIVER_DEFINITIONS[driverName]
    || DRIVER_DEFINITIONS[driver]
    || (shortCode && MEDDPICC_DEFINITIONS[shortCode])
    || (driverLower && MEDDPICC_DEFINITIONS[driverLower])

  return (
    <Tooltip
      placement="top"
      arrow
      classes={{ tooltip: classes.customTooltip }}
      title={title || 'No Driver Definition Found'}
    >
      <Icon component={InfoOutlinedIcon} className={classes.active} />
    </Tooltip>
  );
};

export default CustomTooltip;
